import * as R from "ramda";
import * as React from "react";
import styled from "styled-components";

import { config } from "../../../../config";
import { fmtDateToLocalZone } from "../../../../utils";
import { fullCourseViews } from "../../../../utils/courseViews";

import { CourseChanges, IClientPatch, IPatchAction } from "../../../course-author/helpers/patches";
import { ICoursePublication, ICourseResolveDisplayResponse, IFullCourse } from "../../../course-author/types";
import ClickableElement, { ClickableLink } from "../../../utils/ClickableElement";
import CustomPagination from "../../../utils/Pagination/NumberedPagination";
import { Table } from "../../../utils/Pagination/NumberedPagination/table";
import ToggleDisplay from "../../../utils/ToggleDisplay";

import { ChangeDescription, LocationDescription } from "./utils";

export interface IChangesPaginationProps {
  changes: CourseChanges;
  course: IFullCourse;
  courseId: string;
  editors: {
    [id: string]: ICourseResolveDisplayResponse;
  };
  fetchNextPage: (count: number) => void;
  maxChanges: number;
  pageIndex: number;
  perPage: number;
  publishVersion: (versionId?: number) => () => void;
  renderHeaderRow?: () => JSX.Element;
  unpublishVersion: (versionId?: number) => () => void;
  userId: string;
}

const Container = styled.div`
  overflow-x: scroll;
  margin-top: 1rem;
`;

const TopPagination = styled(CustomPagination)`
  display: none;
  margin-top: 2rem;
  margin-bottom: 1rem;

  @media (${({ theme }) => theme.breakpoints.min800}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const BottomPagination = styled(CustomPagination)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
`;

const PreviewLink = styled(ClickableLink)`
  display: inline-block;
  margin: 0 auto;
  text-decoration: underline;
`;

const PButtonContainer = styled.div`
  display: block;
`;

const PublishButton = styled(ClickableElement)(
  ({ theme }) => `
  background: ${theme.colors.white};
  border: 0.2rem solid ${theme.colors.buttonBlue};
  border-radius: 0.5rem;
  box-shadow: 0.3rem 0.3rem 0.3rem rgba(0, 0, 0, 0.4);
  color: ${theme.colors.buttonBlue};
  font-weight: bold;
  justify-content: space-around;
`
);

const PublishedLabel = styled(ToggleDisplay)`
  width: 100%;
  text-align: center;
`;

interface IDisplayVersion {
  courseId: string;
  version: number;
  userId: string;
}

const GoToVersionLink: React.FC<IDisplayVersion> = ({ version, courseId, userId }) => {
  const previewUrl = `${config.saltcomplianceApiRoot}/shc/courses/${courseId}/_preview/version/${version}?activeUser=${userId}`;

  return (
    <PreviewLink href={previewUrl} target="_blank">
      <strong>{version}</strong>
    </PreviewLink>
  );
};

interface IDisplayActions {
  isPublished: boolean;
  isPublishable: boolean;
  isUnpublishable: boolean;
  publishVersion: () => void;
  unpublishVersion: () => void;
}

const PublicActionButton: React.FC<IDisplayActions> = ({
  isPublished,
  isPublishable,
  isUnpublishable,
  publishVersion,
  unpublishVersion,
}) => (
  <PButtonContainer>
    <ToggleDisplay shouldDisplay={isPublishable}>
      <PublishButton onClick={publishVersion}>Publish</PublishButton>
    </ToggleDisplay>
    <ToggleDisplay shouldDisplay={isPublished && isUnpublishable}>
      <PublishButton onClick={unpublishVersion}>Unpublish</PublishButton>
    </ToggleDisplay>
    <PublishedLabel shouldDisplay={isPublished && !isUnpublishable}>Published</PublishedLabel>
  </PButtonContainer>
);

interface IChangesTable {
  action: React.ReactNode;
  change: React.ReactNode;
  dateOfChange: string;
  editedBy: React.ReactNode;
  location: React.ReactNode;
  version: React.ReactNode;
}

const ChangesPagination: React.SFC<IChangesPaginationProps> = props => {
  const { changes, pageIndex, perPage, fetchNextPage, maxChanges } = props;
  const patchesDisplayed = changes.slice(pageIndex * perPage, (pageIndex + 1) * perPage);

  const pageCount = Math.floor(maxChanges / perPage) + 1;

  if (fetchNextPage && pageIndex === pageCount - 1 && changes.length < maxChanges) {
    fetchNextPage(perPage);
  }

  const publishedVersions: number[] = R.compose<
    ICoursePublication[],
    ICoursePublication[],
    number[],
    number[],
    number[]
  >(
    R.uniq,
    R.sort<number>((a, b) => (a < b ? -1 : 1)),
    R.map((pub: ICoursePublication) => pub.revision),
    R.filter((pub: ICoursePublication) => !pub.deleted)
  )(fullCourseViews.publications(props.course) || []);
  const lastPublication = R.last(publishedVersions);

  const isPublishable = (patch: IClientPatch<IPatchAction>): boolean =>
    R.all(vId => vId < patch.versionId, publishedVersions);
  const isPublished = (patch: IClientPatch<IPatchAction>): boolean =>
    R.any(vId => vId === patch.versionId, publishedVersions);
  const isUnpublishable = (patch: IClientPatch<IPatchAction>): boolean => lastPublication === patch.versionId;

  const toTableRow = (change: IClientPatch<IPatchAction>): IChangesTable => {
    const editor = props.editors[change.createdBy];
    const displayName = R.pathOr("Loading...", ["displayName"], editor);
    return {
      action: (
        <PublicActionButton
          isPublishable={isPublishable(change)}
          isPublished={isPublished(change)}
          isUnpublishable={isUnpublishable(change)}
          publishVersion={props.publishVersion(change.versionId)}
          unpublishVersion={props.unpublishVersion(change.versionId)}
        />
      ),
      change: <ChangeDescription course={props.course} change={change} />,
      dateOfChange: fmtDateToLocalZone(change.createdAt),
      editedBy: <strong>{displayName}</strong>,
      location: <LocationDescription course={props.course} change={change} />,
      version: <GoToVersionLink courseId={props.courseId} userId={props.userId} version={change.versionId} />,
    };
  };

  return (
    <>
      <TopPagination total={maxChanges} />
      <Container>
        <Table
          columns={[
            { name: "Version", accessor: "version" },
            { name: "Change", accessor: "change" },
            { name: "Location", accessor: "location" },
            { name: "Date Of Change", accessor: "dateOfChange" },
            { name: "Edited By", accessor: "editedBy" },
            { name: "Action", accessor: "action" },
          ]}
          data={patchesDisplayed.map(toTableRow)}
        />
      </Container>
      <BottomPagination total={maxChanges} />
    </>
  );
};

export default ChangesPagination;
